import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { UserSelectors, Customer } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable, forkJoin, of } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  shareReplay,
  skipWhile,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import {
  OrderOptions,
  PageAndSize,
  Paginated,
  SearchOptions,
} from 'src/app/delivery-data/models/paginated-deliveries.model';

import { Environment } from 'src/environments/environment.model';

import { HierarchicalDataColumn } from '../../shared/hierarchical-data/hierarchical-data.model';
import {
  Invoice,
  InvoiceTotal,
  GetInvoiceTotalsResponse,
  InvoiceColumn,
  DetailedInvoice,
  InvoiceShipToReference,
} from '../invoice-data.model';

@Injectable({
  providedIn: 'root',
})
export class InvoiceDataService {
  private readonly env = inject<Environment>(EnvironmentInjector);
  protected baseUrl = `${this.env.API_BASE_URL}/service/invoice-v2/secure`;
  private readonly customerId$ = this.store.select(UserSelectors.selectCustomer);
  private readonly invoiceBatchPageSize = 1000;
  private readonly defaultInvoiceOrder: OrderOptions = {
    column: 'InvoiceDate',
    descending: true,
  };

  constructor(
    private readonly store: Store<RootState>,
    private readonly http: HttpClient,
    private readonly localeService: LocaleService,
  ) {}

  public getInvoices(startDate: string, endDate: string): Observable<Invoice[]> {
    return this.resolveShipToIds().pipe(
      switchMap((shipToIds) => this.getAllInvoicesPaginated(startDate, endDate, shipToIds)),
    );
  }

  public getRecentInvoices(startDate: Date, endDate: Date): Observable<Invoice[]> {
    return this.getInvoicesPaginated(startDate, endDate, {
      pageIndex: 0,
      pageSize: 3,
    }).pipe(map(({ results }) => results));
  }

  public getInvoiceShipTos(
    startDate: string,
    endDate: string,
  ): Observable<InvoiceShipToReference[]> {
    return this.customerId$.pipe(
      skipWhile((customer) => !customer),
      take(1),
      switchMap(() =>
        this.http
          .get<InvoiceShipToReference[]>(`${this.baseUrl}/invoice/ship-tos`, {
            params: { StartDate: startDate, EndDate: endDate },
          })
          .pipe(
            catchError((error: HttpErrorResponse) => {
              this.store.dispatch(NotificationActions.errorSnackbar({ message: error.message }));
              return of([] as InvoiceShipToReference[]);
            }),
          ),
      ),
    );
  }

  private getAllInvoicesPaginated(
    startDate: string,
    endDate: string,
    shipToIds: number[],
  ): Observable<Invoice[]> {
    return this.fetchInvoicesPage(
      startDate,
      endDate,
      {
        pageIndex: 0,
        pageSize: this.invoiceBatchPageSize,
      },
      shipToIds,
    ).pipe(
      switchMap((firstPage) => {
        const remainingRequests = Array.from(
          { length: Math.max(firstPage.metadata.totalPages - 1, 0) },
          (_, index) =>
            this.fetchInvoicesPage(
              startDate,
              endDate,
              {
                pageIndex: index + 1,
                pageSize: this.invoiceBatchPageSize,
              },
              shipToIds,
            ),
        );

        return forkJoin([of(firstPage), ...remainingRequests]);
      }),
      map((pages) => pages.flatMap((page) => page.results)),
    );
  }

  public getInvoiceTotals(
    startDate: string,
    endDate: string,
    selectedLocations: number[],
  ): Observable<InvoiceTotal[]> {
    return this.customerId$.pipe(
      skipWhile((customerId) => !customerId),
      take(1),
      mergeMap((customer: Customer) => {
        if (!customer) {
          return of([] as InvoiceTotal[]);
        }

        const shipToIds = (selectedLocations ?? []).map((location) => location.toString());

        return this.http
          .get<GetInvoiceTotalsResponse>(`${this.baseUrl}/invoice/totals`, {
            params: {
              CustomerId: customer.id.toString(),
              StartDate: startDate,
              EndDate: endDate,
              ShipToIds: shipToIds,
            },
          })
          .pipe(map((response: GetInvoiceTotalsResponse) => response.invoiceTotals));
      }),
    );
  }

  public getInvoiceDocument(ids: number[], shipToIds?: number[]): Observable<HttpResponse<Blob>> {
    return this.customerId$.pipe(
      take(1),
      mergeMap((customer: Customer) => {
        if (!customer) {
          return of({} as HttpResponse<Blob>);
        }

        const uniqueShipToIds = [
          ...new Set(
            (shipToIds ?? customer.shipTos.map((shipTo) => shipTo.id)).map((shipToId) =>
              shipToId.toString(),
            ),
          ),
        ];

        return this.http.get(
          `${this.env.API_BASE_URL}/service/document/secure/document?DocumentType=Invoice`,
          {
            params: {
              Ids: ids.map((id) => id.toString()),
              ShipToIds: uniqueShipToIds,
            },
            responseType: 'blob',
            observe: 'response',
          },
        );
      }),
    );
  }

  public getHeadings(columns: InvoiceColumn[], prefix: string): HierarchicalDataColumn[] {
    return columns.map((col: string) => ({
      value: this.localeService.translate(`${prefix}${col}`),
      id: col,
    }));
  }

  public filterAndSortInvoices(invoices: Invoice[], shipToIds: number[]) {
    return invoices
      .filter((i: Invoice) => shipToIds.length === 0 || shipToIds?.indexOf(i?.shipTo?.id) !== -1)
      .sort((i1: Invoice, i2: Invoice) => (i1.invoiceDate < i2.invoiceDate ? 1 : -1));
  }

  public filterAndSortDetailedInvoices(detailedInvoices: DetailedInvoice[], shipToIds: number[]) {
    return detailedInvoices
      .filter(
        (d: DetailedInvoice) => shipToIds.length === 0 || shipToIds?.indexOf(d?.shipTo?.id) !== -1,
      )
      .sort((d1: DetailedInvoice, d2: DetailedInvoice) =>
        d1.invoiceDate < d2.invoiceDate ? 1 : -1,
      );
  }

  public getInvoicesPaginated(
    startDate: Date,
    endDate: Date,
    paginationOptions: PageAndSize,
    search?: SearchOptions,
    shipToIds?: number[],
    order?: OrderOptions,
    isExport = false,
  ): Observable<Paginated<Invoice>> {
    const { pageIndex, pageSize } = paginationOptions;

    return this.resolveShipToIds(shipToIds).pipe(
      tap((_) =>
        isExport ? this.store.dispatch(CoreActions.setLoading({ loading: true })) : undefined,
      ),
      map((resolvedShipToIds: number[]) =>
        this.createInvoiceRequestBody(
          startDate.toISOString(),
          endDate.toISOString(),
          {
            pageIndex,
            pageSize,
          },
          resolvedShipToIds,
          search?.term,
          order,
        ),
      ),
      switchMap((body) => this.http.post<Paginated<Invoice>>(`${this.baseUrl}/invoice`, body)),
      tap((_) =>
        isExport ? this.store.dispatch(CoreActions.setLoading({ loading: false })) : undefined,
      ),
      catchError((error) => {
        this.store.dispatch(NotificationActions.errorSnackbar({ message: error.message }));
        if (isExport) {
          this.store.dispatch(CoreActions.setLoading({ loading: false }));
        }
        return of({
          metadata: {
            currentPage: 0,
            pageSize: 0,
            totalResultCount: 0,
            totalPages: 0,
          },
          results: [],
        });
      }),
      shareReplay(1),
    );
  }

  private resolveShipToIds(shipToIds?: number[]): Observable<number[]> {
    return this.customerId$.pipe(
      skipWhile((customer) => !customer && !shipToIds),
      take(1),
      map(() => shipToIds ?? []),
    );
  }

  private fetchInvoicesPage(
    startDate: string,
    endDate: string,
    paginationOptions: PageAndSize,
    shipToIds: number[],
    searchTerm?: string,
    order?: OrderOptions,
  ): Observable<Paginated<Invoice>> {
    return this.http.post<Paginated<Invoice>>(
      `${this.baseUrl}/invoice`,
      this.createInvoiceRequestBody(
        startDate,
        endDate,
        paginationOptions,
        shipToIds,
        searchTerm,
        order,
      ),
    );
  }

  private createInvoiceRequestBody(
    startDate: string,
    endDate: string,
    paginationOptions: PageAndSize,
    shipToIds: number[],
    searchTerm?: string,
    order?: OrderOptions,
  ) {
    const { pageIndex, pageSize } = paginationOptions;

    return {
      startDate,
      endDate,
      pageIndex,
      pageSize,
      shipToIds,
      order: order ?? this.defaultInvoiceOrder,
      searchTerm,
    };
  }
}
